import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notAvailableFeature } from './cycleStatus'
import CycleOverviewSection from './CycleOverviewSection'

// VH-227: report tabs. VH-228/223/224 fill a slot by passing a renderer in
// `reportRenderers[key]`; until then a skipped step shows "not available yet".
const REPORT_TABS = [
  { key: 'followup', step: 'followup' },
  { key: 'health_report', step: 'health_report' },
  { key: 'nutrition_report', step: 'nutrition_report' },
  // VH-228 phase 0: stored documents are not a step; the tab is always there.
  { key: 'documents', step: null },
]

const PAYLOAD_SECTIONS = ['recommendations', 'nutrition_targets', 'training_plan', 'supplements', 'markers', 'analyses']

export function CycleDraftView({ version, steps = [], reportRenderers = {}, initialTab = 'overview' }) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(() => (REPORT_TABS.some((x) => x.key === initialTab) ? initialTab : 'overview'))
  const payload = version?.payload ?? {}
  const stepByName = Object.fromEntries(steps.map((s) => [s.step, s]))
  const tabs = [{ key: 'overview' }, ...REPORT_TABS]

  const renderReportTab = (tab) => {
    const step = stepByName[tab.step]
    const feature = notAvailableFeature(step)
    const render = reportRenderers[tab.key]
    const rendered = render && !feature ? render({ version, step, openTab: setActiveTab }) : null
    if (rendered) return rendered
    return (
      <div className="text-center py-10" data-testid={`report-slot-${tab.key}`}>
        <p className="text-sm font-medium text-gray-700">
          {feature
            ? t('healthCycle.draft.notAvailableYet')
            : step && ['pending', 'running'].includes(step.status)
              ? t('healthCycle.steps.status.running')
              : t('healthCycle.draft.noReport')}
        </p>
        {feature && <p className="text-xs text-gray-500 mt-1">{t('healthCycle.draft.comingWith', { feature })}</p>}
      </div>
    )
  }

  return (
    <section className="card">
      <div className="border-b border-gray-200 px-4 overflow-x-auto">
        <nav className="-mb-px flex space-x-6" aria-label={t('healthCycle.draft.tabsLabel')}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 px-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(`healthCycle.draft.tabs.${tab.key}`)}
            </button>
          ))}
        </nav>
      </div>
      <div className="card-body">
        {activeTab === 'overview' ? (
          version ? (
            <div className="grid gap-4 md:grid-cols-2">
              {PAYLOAD_SECTIONS.map((section) => (
                <article key={section} className="border border-gray-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    {t(`healthCycle.sections.${section}`)}
                  </h4>
                  <CycleOverviewSection section={section} payload={payload} />
                </article>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('healthCycle.draft.noVersion')}</p>
          )
        ) : (
          renderReportTab(tabs.find((x) => x.key === activeTab))
        )}
      </div>
    </section>
  )
}

export default CycleDraftView
